import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { CalendarCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { copilotApi, type CopilotProposal } from './copilotApi';
import {
  detailLabel, detailValue, isPastExpiry, proposalDisplayState, proposalErrorText, proposalResultText, proposalStatusText,
  proposalTitle, type ProposalDisplayState, type ProposalResult,
} from './copilotText';

type State = ProposalDisplayState | 'working' | 'failed' | 'unavailable';

/** Failures after which the card stays open on the server (still PENDING): Confirm / Cancel can be tried again. */
function stillPending(code: string | undefined): boolean {
  return !['NOT_FOUND', 'NOT_ALLOWED', 'BAD_REQUEST', 'SIGNED_OUT'].includes(code ?? '');
}

/**
 * A change the Copilot suggests (book, reschedule, cancel, follow-up, reminder, re-order draft). Nothing happens
 * until the user presses Confirm, which calls the server with their normal permissions (doc 154, D5).
 *
 * Confirm and Cancel are enabled only once the answer's turn has finished (`turnDone`): the server saves the
 * card's parameters at the end of the turn and answers 409 CONVERSATION_BUSY before that. A stored card shows its
 * status (Booked / Sent, Cancelled, Expired); a pending one that has not expired can still be confirmed.
 */
export function ProposalCard({ proposal, conversationId, turnDone, turnUnfinished }: {
  proposal: CopilotProposal;
  conversationId?: string | null;
  /** The turn that made this card ended with `done` (always true for a stored card). */
  turnDone: boolean;
  /** The turn ended without `done` (stopped or failed): the card was never saved and cannot be confirmed. */
  turnUnfinished?: boolean;
}) {
  const { t, i18n } = useTranslation('copilot');
  const [state, setState] = useState<State>(() => proposalDisplayState(proposal));
  const [message, setMessage] = useState<string | null>(null);
  // The confirm / cancel answer, kept as key + values so the line follows the UI language (also after a switch).
  const [result, setResult] = useState<ProposalResult | null>(null);

  // A pending card turns into "Expired" on screen when its time runs out.
  useEffect(() => {
    if (state !== 'pending') return undefined;
    if (!proposal.expiresAt) return undefined;
    const ms = Date.parse(proposal.expiresAt) - Date.now();
    if (!Number.isFinite(ms)) return undefined;
    if (ms <= 0) {
      setState('expired');
      return undefined;
    }
    const timer = window.setTimeout(() => setState('expired'), Math.min(ms, 2_147_000_000));
    return () => window.clearTimeout(timer);
  }, [state, proposal.expiresAt]);

  const decide = async (decision: 'confirm' | 'cancel') => {
    if (isPastExpiry(proposal.expiresAt)) {
      setState('expired');
      return;
    }
    setState('working');
    setMessage(null);
    setResult(null);
    const out = await copilotApi.decideProposal(proposal.id, decision, proposal.conversationId ?? conversationId);
    if (out.unavailable) {
      setState('unavailable');
      return;
    }
    if (!out.ok) {
      switch (out.code) {
        case 'PROPOSAL_EXPIRED': setState('expired'); return;
        case 'ALREADY_CONFIRMED': setState('confirmed'); setMessage(proposalErrorText(out.code, out.message, t)); return;
        case 'ALREADY_CANCELLED': setState('cancelled'); return;
        default:
          setState(stillPending(out.code) ? 'pending' : 'failed');
          setMessage(proposalErrorText(out.code, out.message, t));
          return;
      }
    }
    if (decision === 'confirm' || out.status === 'CONFIRMED') {
      setState('confirmed');
      // The confirm line names what was done ("Booked Ravi Kumar with Dr Rao, tomorrow, 10:30 am").
      setResult({ messageKey: out.messageKey, messageParams: out.messageParams, message: out.message });
    } else {
      setState('cancelled');
      setResult({ messageKey: out.messageKey, messageParams: out.messageParams });
    }
  };

  const details = Object.entries(proposal.details ?? {})
    .map(([k, v]) => [k, detailValue(v, t)] as const)
    .filter((e): e is readonly [string, string] => e[1] !== null);
  const titleId = `copilot-proposal-${proposal.id}`;
  const open = state === 'pending' || state === 'working';
  const canAct = open && turnDone && !turnUnfinished;
  const error = (state === 'pending' || state === 'failed') ? message : null;

  return (
    <Paper variant="outlined" component="section" aria-labelledby={titleId} sx={{ p: 1.5, borderRadius: 2, mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: details.length ? 1 : 0 }}>
        <CalendarCheck size={16} aria-hidden />
        <Typography id={titleId} variant="subtitle2" sx={{ fontWeight: 600 }}>
          {proposalTitle(proposal, t, i18n)}
        </Typography>
      </Stack>
      {details.length > 0 && (
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5 }}>
          {details.map(([k, v]) => (
            <Box key={k} sx={{ display: 'contents' }}>
              <Typography component="dt" variant="caption" color="text.secondary">{detailLabel(k, t, i18n)}</Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0 }}>{v}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {open && !turnUnfinished && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <Button
            size="small" variant="contained" disableElevation onClick={() => decide('confirm')}
            disabled={!canAct || state === 'working'}
            startIcon={state === 'working' ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {t('proposal.confirm')}
          </Button>
          <Button size="small" onClick={() => decide('cancel')} disabled={!canAct || state === 'working'}>
            {t('proposal.cancel')}
          </Button>
          {!turnDone && (
            <Typography variant="caption" color="text.secondary">{t('proposal.waiting')}</Typography>
          )}
        </Stack>
      )}
      {open && turnUnfinished && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('proposal.unfinished')}</Typography>
      )}
      <Box role="status" aria-live="polite">
        {state === 'confirmed' && (
          <Alert severity="success" sx={{ mt: 1.5, py: 0 }}>
            {message || (result && proposalResultText(result, t, i18n)) || proposalStatusText('confirmed', proposal.action, t, i18n)}
          </Alert>
        )}
        {state === 'cancelled' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {(result && proposalResultText(result, t, i18n)) || proposalStatusText('cancelled', proposal.action, t, i18n)}
          </Typography>
        )}
        {state === 'expired' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {proposalStatusText('expired', proposal.action, t, i18n)}
          </Typography>
        )}
        {state === 'unavailable' && <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>{t('proposal.unavailable')}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>{error}</Alert>}
      </Box>
    </Paper>
  );
}
